import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Property } from 'shared-types/Property';
import { ConfigurationGroupType, SpaPackageType } from 'shared-types/SpaPackage';
import type { ExtrasGuest, ExtrasItem } from 'voyager';
import { ExtrasTemplate } from 'voyager';

import { Spinner } from '@common/components/Spinner';
import { useExtrasLabels } from '@features/spa/hooks';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';

import { SpaPackageFormValues } from '../SpaPackageForm.schema';
import { adaptExtras } from '../SpaPackageModal/SpaPackageExtras/utils';
import { calculateExtraPrices } from '../SpaPackageModal/SpaPackageModal.helpers';
import { ExtrasFormDataType } from '../SpaPackageModal/SpaPackageModal.types';
import { calculateTotalPrice } from './SpaDayBooking.helpers';
import { useSubmitBooking } from './hooks/useSubmitBooking';

type AdaptedExtra = ReturnType<typeof adaptExtras>[number];

function findExtra(adaptedExtras: AdaptedExtra[], extraId: string): AdaptedExtra | undefined {
  return adaptedExtras.find((o) => String(o.extra?.id || o.id) === extraId);
}

function getExtraId(opt: AdaptedExtra): string | number {
  return opt.extra?.id || opt.id;
}

function getFieldKey(opt: AdaptedExtra): string {
  return `id-${getExtraId(opt)}`;
}

type SpaDayExtrasProps = {
  extras: ConfigurationGroupType[];
  entity: SpaPackageType;
  property: Property;
  language: string;
  id: number;
  onClose: (showMiniBasket: boolean) => void;
  onBack: () => void;
};

export const SpaDayExtras: FC<SpaDayExtrasProps> = ({
  extras,
  entity,
  property,
  language,
  id,
  onClose,
  onBack,
}) => {
  const { t } = useTranslations();
  const extrasLabels = useExtrasLabels();
  const form = useFormContext<SpaPackageFormValues>();
  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedExtras = useWatch({ control: form.control, name: 'extras' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );

  const { handleSubmit } = useSubmitBooking({
    form,
    entity,
    propertyId: property.id,
    language,
    id,
    onClose,
  });

  const adaptedExtras = useMemo(() => adaptExtras(extras), [extras]);

  const selectedGuests = useMemo(
    () =>
      (watchedGuests || []).map(({ title, firstName, lastName, gender }) => ({
        title,
        firstName,
        lastName,
        gender,
      })),
    [watchedGuests]
  );

  const getGuestLabel = useCallback(
    (guest: { title: string; firstName: string; lastName: string }) =>
      `${guest.title} ${guest.firstName} ${guest.lastName}`.trim(),
    []
  );

  const guests: ExtrasGuest[] = useMemo(
    () =>
      selectedGuests.map((guest, index) => ({
        id: String(index),
        label: getGuestLabel(guest),
      })),
    [selectedGuests, getGuestLabel]
  );

  const items: ExtrasItem[] = useMemo(
    () =>
      adaptedExtras.map((opt) => {
        const fieldKey = getFieldKey(opt);
        const selections = watchedExtras?.[fieldKey];
        const isSelected = Array.isArray(selections) && selections.length > 0;

        const selectedGuestIds = isSelected
          ? selections
              .map((s: { label: string }) => {
                const guestIndex = guests.findIndex((g) => g.label === s.label);
                return guestIndex >= 0 ? String(guestIndex) : null;
              })
              .filter((gid: string | null): gid is string => gid !== null)
          : undefined;

        return {
          id: String(getExtraId(opt)),
          image: { src: opt.image?.url, alt: opt.name || '' },
          title: opt.name || '',
          price: {
            amount: opt.price?.amount || 0,
            currencyCode: opt.price?.currencyCode || 'GBP',
          },
          selected: isSelected,
          quantity: isSelected ? selections.length : undefined,
          selectedGuestIds,
        };
      }),
    [adaptedExtras, watchedExtras, guests]
  );

  const handleAdd = useCallback(
    (itemId: string) => {
      const opt = findExtra(adaptedExtras, itemId);
      if (!opt) return;

      const extraId = getExtraId(opt);
      const fieldKey = getFieldKey(opt);

      if (selectedGuests.length > 1) {
        const allGuestOptions = selectedGuests.map((guest) => ({
          label: getGuestLabel(guest),
          value: guest,
          price: opt.price,
          id: extraId,
        }));
        form.setValue(`extras.${fieldKey}` as `extras.${string}`, allGuestOptions);
      } else {
        const guestOption = {
          label: selectedGuests.map((g) => getGuestLabel(g)).join(', '),
          value: selectedGuests[0] || {},
          price: opt.price,
          id: extraId,
        };
        form.setValue(`extras.${fieldKey}` as `extras.${string}`, [guestOption]);
      }
    },
    [form, adaptedExtras, selectedGuests, getGuestLabel]
  );

  const handleRemove = useCallback(
    (itemId: string) => {
      const opt = findExtra(adaptedExtras, itemId);
      if (!opt) return;
      form.setValue(`extras.${getFieldKey(opt)}` as `extras.${string}`, undefined);
    },
    [form, adaptedExtras]
  );

  const handleConfirmGuestSelection = useCallback(
    (itemId: string, selectedGuestIds: string[]) => {
      const opt = findExtra(adaptedExtras, itemId);
      if (!opt) return;

      const extraId = getExtraId(opt);
      const fieldKey = getFieldKey(opt);

      if (selectedGuestIds.length === 0) {
        form.setValue(`extras.${fieldKey}` as `extras.${string}`, undefined);
      } else {
        const entries = selectedGuestIds.map((gid) => {
          const guestIndex = Number(gid);
          const guest = selectedGuests[guestIndex];
          return { label: getGuestLabel(guest), value: guest, price: opt.price, id: extraId };
        });
        form.setValue(`extras.${fieldKey}` as `extras.${string}`, entries);
      }
    },
    [form, adaptedExtras, selectedGuests, getGuestLabel]
  );

  const guestsCount = watchedGuests?.length || 0;
  const extrasTotal = calculateExtraPrices(
    Object.fromEntries(
      Object.entries(watchedExtras || {}).filter(([, v]) => Array.isArray(v))
    ) as ExtrasFormDataType
  );

  const displayedPrice = watchedArrivalTime?.price || entity.price;
  const totalPrice = calculateTotalPrice(displayedPrice, guestsCount, extrasTotal);

  const slotDiscount = watchedArrivalTime?.discount?.discountedPrice;
  const entityDiscount = entity.discount?.discountedPrice;
  const discountedPrice = slotDiscount || entityDiscount;
  const discountedTotalPrice = discountedPrice
    ? calculateTotalPrice(discountedPrice, guestsCount, extrasTotal)
    : null;

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <ExtrasTemplate
        items={items}
        totalAmount={discountedTotalPrice ? discountedTotalPrice.amount : totalPrice.amount}
        originalAmount={discountedTotalPrice ? totalPrice.amount : undefined}
        currencyCode={totalPrice.currencyCode}
        guests={guests.length > 1 ? guests : undefined}
        onBack={onBack}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onConfirmGuestSelection={handleConfirmGuestSelection}
        onCtaClick={handleSubmit}
        currentStep={2}
        totalSteps={2}
        labels={{
          title: t('spa.package.extrasTitle'),
          ctaLabel: t('booking.continue'),
          footerTitle: entity.name,
          ...extrasLabels,
        }}
      />
    </>
  );
};
